import { BehaviorSubject, EMPTY, Observable, scan, Subscription } from 'rxjs';
import { catchError, finalize, map, tap } from "rxjs/operators";
import {
  AiChart,
  AiChatWithCharts, AiHistoryMessage, AiRequestDto, AiResponseEvent,
  MessageType,
} from 'src/app/domain/global-ai/utils';
import { AbstractAiBackendService } from './abstract-ai-backend.service';
import { AI_STRINGS } from "./utils";
import { random } from "lodash";

/**
 * Abstract base service for AI chat functionality with streaming responses and chart visualization support.
 *
 * This service manages the state and interactions for AI chat interfaces, including:
 * - Maintaining message history
 * - Processing streaming responses
 * - Handling chart data from AI responses
 * - Managing conversation state
 *
 * Extend this class to create specialized AI chat services for different application domains.
 *
 * @example
 * export class GlobalAiService extends AbstractAiService {
 *   constructor(protected backend: GlobalAiBackendService) {
 *     super(backend);
 *   }
 *
 *   // add specialized methods if needed
 * }
 */
export abstract class AbstractAiService {
  private currentStreamSubscription: Subscription;
  protected messagesSubject = new BehaviorSubject<AiHistoryMessage[]>([]);

  protected chartsSubject = new BehaviorSubject<AiChart[]>([]);

  /**
   * Observable stream of AI chat messages.
   * Components should subscribe to this to display message history.
   */
  messages$ = this.messagesSubject.asObservable();

  /**
   * Observable stream of chart data received from AI responses.
   * Components should subscribe to this to display charts.
   */
  charts$ = this.chartsSubject.asObservable();

  hasActiveRequest = false;

  protected constructor(protected backendService: AbstractAiBackendService) {}

  /**
   * Sends a streaming request to the AI service and processes both text chunks
   * and chart data from the response stream.
   *
   * This method:
   * 1. Cancels any active request
   * 2. Adds the user message to the message history
   * 3. Creates a "thinking" placeholder for the AI response
   * 4. Processes the streaming response, updating the UI as chunks arrive
   * 5. Handles any charts included in the response
   *
   * @param request - The AI request containing the user's query and context data
   *
   * @example
   * // inside a component:
   * const request = new YourAiRequestDto('Analyze revenue trends for Q1');
   * this.aiService.sendStreamingRequestWithCharts(request);
   */
  sendStreamingRequestWithCharts(request: AiRequestDto): void {
    // cancel any active streaming request before starting a new one
    this.cancelActiveRequest();

    // 1) add user message
    const userMessage = new AiHistoryMessage(MessageType.USER, request.request);
    this.appendMessage(userMessage);

    // 2) prepare assistant message, which will be updated while streaming
    const assistantMessage = new AiHistoryMessage(MessageType.ASSISTANT, 'Thinking...', true);
    this.appendMessage(assistantMessage);

    this.currentStreamSubscription = this.backendService.sendRequestStreamWithCharts(request).pipe(
      // process chart JSON (if provided)
      tap((event: AiResponseEvent): void => {
        this.hasActiveRequest = true;
        if (event.json) {
          try {
            // currently we only sent charts as jsons, to it's an AiChart
            const parsedChart = JSON.parse(event.json) as AiChart;
            // push chart to the current charts subject.
            const currentCharts = [...this.chartsSubject.value];
            this.ensureUniqueChartId(parsedChart, currentCharts);
            currentCharts.push(parsedChart);
            this.chartsSubject.next(currentCharts);
          } catch (error) {
            console.error('Could not parse chart JSON: ', error);
          }
        }
      }),
      // accumulate the text chunks from the stream, excluding debugging chunks
      scan((accumulatedText: string, event: AiResponseEvent): string => {
        if (event.textChunk) {
          return accumulatedText + event.textChunk;
        }
        return accumulatedText;
      }, ''),
      // if any error occurs, update the assistant message and recover
      catchError(() => {
        assistantMessage.text = AI_STRINGS.error;
        assistantMessage.isThinking = false;
        this.updateLastAssistantMessage(assistantMessage);
        return EMPTY;
      }),
      finalize(() => {
        this.hasActiveRequest = false;
      })
    ).subscribe((accumulatedText: string): void => {
      // update the assistant message progressively with the accumulated text
      assistantMessage.text = accumulatedText;
      assistantMessage.isThinking = false;
      this.updateLastAssistantMessage(assistantMessage);
    });
  }

  cancelActiveRequest(): void {
    if (this.currentStreamSubscription) {
      this.currentStreamSubscription.unsubscribe();
      this.currentStreamSubscription = undefined;
    }
  }

  /**
   * Fetches conversation history from the backend for a given conversation ID.
   * This populates both the messages and charts observables with historical data.
   *
   * @param id - The conversation identifier (string or number)
   *
   * @example
   * this.aiService.fetchConversationHistory('your-conv-id');
   * // if component is subscribed to {@link messages$} and/or {@link charts$}, the component will get updated values.
   */
  fetchConversationHistory(id: string | number): void {
    this.backendService.fetchConversationHistory(id)
      .pipe(
        map((messages: AiHistoryMessage[]): AiChatWithCharts => {
          const charts: AiChart[] = [];
          messages.forEach((msg: AiHistoryMessage): void => {
            if (msg.jsonBlocks && msg.jsonBlocks.length > 0) {
              msg.jsonBlocks.forEach((jsonStr: string): void => {
                try {
                  const chart = JSON.parse(jsonStr) as AiChart;
                  this.ensureUniqueChartId(chart, charts);
                  charts.push(chart);
                } catch (error) {
                  console.error('Error parsing chart JSON:', error);
                }
              });
            }
          });
          return new AiChatWithCharts(messages, charts);
        }),
        tap((chatHistory: AiChatWithCharts) : void => {
          this.messagesSubject.next(chatHistory.messages);
          this.chartsSubject.next(chatHistory.charts);
        }),
        catchError((error) => {
          console.error('Error fetching conversation history:', error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /** Clears the chat history both on the backend and in the local state */
  clearChat(id: string | number): Observable<void> {
    this.cancelActiveRequest();
    this.clearObservables();
    return this.backendService.clearHistory(id);
  }

  /** Resets the local messages, charts, and debugging chunks without affecting the backend */
  clearObservables(): void {
    this.messagesSubject.next([]);
    this.chartsSubject.next([]);
  }

  /**
   * Updates the most recent AI assistant message in the message history.
   * Used internally to update streaming message content.
   */
  protected updateLastAssistantMessage(newAssistantMsg: AiHistoryMessage): void {
    const currentMessages = [...this.messagesSubject.value];
    const lastIndex = currentMessages.length - 1;
    if (lastIndex >= 0 && currentMessages[lastIndex].role === MessageType.ASSISTANT) {
      currentMessages[lastIndex] = newAssistantMsg;
    } else {
      currentMessages.push(newAssistantMsg);
    }
    this.messagesSubject.next(currentMessages);
  }

  private appendMessage(message: AiHistoryMessage): void {
    const currentMessages = [...this.messagesSubject.value];
    currentMessages.push(message);
    this.messagesSubject.next(currentMessages);
  }

  /** makes sure the chart has unique chartId (llm can mess it up) */
  private ensureUniqueChartId(chart: AiChart, charts: AiChart[]): void {
    if (!chart.chartId) {
      chart.chartId = random(true).toString();
      return;
    }
    const allChartsIds = new Set(charts.map((c) => c.chartId));
    if (allChartsIds.has(chart.chartId)) {
      chart.chartId += random(true).toString();
    }
  }
}
